import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

export const NumbersTab = ({ subId, isAgency }) => {
  const [data, setData] = useState(null);
  const [country, setCountry] = useState("US");
  const [areaCode, setAreaCode] = useState("");
  const [linkNum, setLinkNum] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/calling/numbers?sub_account_id=${subId}`).then(({ data }) => setData(data)), [subId]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => { setBusy(true); try { await fn(); load(); } catch (e) { err(e); } setBusy(false); };
  const buy = () => run(async () => { const { data } = await api.post("/calling/numbers/buy", { sub_account_id: subId, country, area_code: areaCode || null }); toast.success(`Bought ${data.number}`); });
  const link = () => run(async () => { await api.post("/calling/numbers/link", { sub_account_id: subId, number: linkNum }); setLinkNum(""); toast.success("Number added"); });
  const assign = (id, user_id) => run(() => api.put(`/calling/numbers/${id}/assign`, { sub_account_id: subId, user_id: user_id || null }));
  const remove = (id) => run(() => api.delete(`/calling/numbers/${id}?sub_account_id=${subId}`));

  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-primary" />;
  return (
    <div className="space-y-6">
      {!data.configured && <p className="text-sm text-amber-400" data-testid="twilio-not-configured">Twilio isn't connected yet.</p>}
      <Card className="p-6 bg-card border-border/70">
        <h3 className="font-display font-semibold mb-4">Your numbers</h3>
        <div className="space-y-3" data-testid="numbers-list">
          {data.numbers.map((n) => (
            <div key={n.id} className="flex flex-wrap items-center gap-3" data-testid={`number-row-${n.id}`}>
              <Badge variant="secondary">{n.country === "GB" ? "UK" : "US"}</Badge>
              <span className="font-mono text-sm flex-1 min-w-[140px]">{n.number}</span>
              <select disabled={!isAgency || busy} value={n.assigned_user_id || ""} onChange={(e) => assign(n.id, e.target.value)}
                className="h-9 rounded-md bg-secondary/50 border border-border/60 px-2 text-sm" data-testid={`number-assign-${n.id}`}>
                <option value="">Shared (whole team)</option>
                {data.members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              {isAgency && <Button size="sm" variant="ghost" onClick={() => remove(n.id)} data-testid={`number-remove-${n.id}`}><Trash2 className="w-4 h-4" /></Button>}
            </div>
          ))}
          {data.numbers.length === 0 && <p className="text-sm text-muted-foreground">No numbers yet. Add a UK and a US number so each lead is called from a local number.</p>}
        </div>
      </Card>
      {isAgency && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6 bg-card border-border/70 space-y-3">
            <h3 className="font-display font-semibold">Buy a number</h3>
            <div className="flex gap-2">
              <select value={country} onChange={(e) => setCountry(e.target.value)} className="h-10 rounded-md bg-secondary/50 border border-border/60 px-2 text-sm" data-testid="buy-country-select">
                <option value="US">US</option><option value="GB">UK</option>
              </select>
              <Input value={areaCode} onChange={(e) => setAreaCode(e.target.value)} placeholder="Area code (optional)" data-testid="buy-area-code-input" />
            </div>
            <p className="text-xs text-muted-foreground">UK numbers need an approved Twilio regulatory bundle. If yours is approved, buy in Twilio and use "Add existing".</p>
            <Button onClick={buy} disabled={busy || !data.configured} data-testid="buy-number-button">Buy number</Button>
          </Card>
          <Card className="p-6 bg-card border-border/70 space-y-3">
            <h3 className="font-display font-semibold">Add existing Twilio number</h3>
            <Input value={linkNum} onChange={(e) => setLinkNum(e.target.value)} placeholder="+44… or +1…" data-testid="link-number-input" />
            <Button onClick={link} disabled={busy || !linkNum || !data.configured} data-testid="link-number-button">Add number</Button>
          </Card>
        </div>
      )}
    </div>
  );
};
